import api from '../utils/api';
import { getDeviceSignature } from '../utils/deviceSignature';

/**
 * @typedef {Object} LoginResponse
 * @property {string} status - 'success' or 'fail'
 * @property {string} message - Response message
 * @property {string} [token] - JWT Token
 * @property {Object} [user] - User object
 */

/**
 * @typedef {Object} RegisterData
 * @property {string} username
 * @property {string} email
 * @property {string} password
 * @property {string} passwordConfirm
 * @property {string} [firstName]
 * @property {string} [lastName]
 */

export const authService = {
  /**
   * Admin Login
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise<LoginResponse>}
   */
  login: async (email, password) => {
    const response = await api.post('/api/auth/admin-login', {
      email,
      password,
    });
    return response;
  },

  /**
   * General User Login (Student/Instructor)
   * @param {string} email
   * @param {string} password
   * @returns {Promise<LoginResponse>}
   */
  userLogin: async (email, password) => {
    const response = await api.post('/api/auth/login', {
      email,
      password,
      clientFingerprint: { signature: getDeviceSignature() }
    });
    return response;
  },

  /**
   * Register new user
   * @param {RegisterData} userData 
   * @returns {Promise<Object>}
   */
  register: async (userData) => {
    const response = await api.post('/api/auth/register', userData);
    return response;
  },

  /**
   * Check if email exists
   * @param {string} email 
   * @returns {Promise<{exists: boolean}>}
   */
  checkEmailExists: async (email) => {
    const response = await api.post('/api/auth/check-email', { email });
    return response;
  },

   /**
   * Verify Email
   * @param {string} token 
   * @returns {Promise<Object>}
   */
  verifyEmail: async (token) => {
    const response = await api.get(`/api/auth/verify-email?token=${token}`);
    return response;
  },

  /**
   * Get Login History (Protected)
   * @returns {Promise<Array>}
   */
  getLoginHistory: async () => {
    const response = await api.get('/api/auth/login-history');
    return response;
  },

  // Logout (clears token locally)
  logout: () => {
    // AuthContext handles the robust state clearing (including user data).
    // This is just a fallback for non-React contexts if ever needed.
    api.setAuthToken(null);
  },

  // Check if current token is valid
  checkAuth: async () => {
    const response = await api.get('/api/auth/check');
    return response;
  },

  /**
   * Forgot Password - Request password reset email
   * @param {string} email
   * @returns {Promise<Object>}
   */
  forgotPassword: async (email) => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response;
  },

  /**
   * Reset Password with token
   * @param {string} token
   * @param {string} password
   * @param {string} passwordConfirm
   * @returns {Promise<Object>}
   */
  resetPassword: async (token, password, passwordConfirm) => {
    const response = await api.post('/api/auth/reset-password', {
      token,
      password,
      passwordConfirm,
    });
    return response;
  },
};

